import { useAPIContext } from "context/APIContext";
import { useErrorMessageContext } from "context/ErrorModalContext";
import { useFeedbackContext } from "context/FeedbackContext";
import { useFeedbackModalCardContext } from "context/FeedbackModalCardContext";
import { useKEAGraphContext } from "context/KEAGraphContext";
import { dimension, useWindowSizeContext, windowPadding } from "context/WindowSizeContext";
import { ContentView } from "pages/model/preview/content/ContentView";
import { useEffect, useState } from "react";
import { ContentContainer } from "shared/ContentContainer";
import { FeedbackMessage, ValuationType } from "tasks/sample_feedback";

interface Props {}

type PreviewFeedbackEntry = {
  elementId?: string;
  message?: string;
  type?: string;
  category?: string;
};

export const PreviewModelEditor = (_props: Props) => {
  const windowSizeContext = useWindowSizeContext();
  const keaGraphContext = useKEAGraphContext();
  const apiContext = useAPIContext();
  const errorModalContext = useErrorMessageContext();
  const feedbackContext = useFeedbackContext();
  const feedbackModalContext = useFeedbackModalCardContext();

  const [contentDimension, setContentDimension] = useState<dimension>({
    width: 1,
    height: 1,
  });

  const mapTypeToValuation = (value: unknown): ValuationType => {
    if (typeof value !== "string") {
      return ValuationType.OTHER;
    }
    switch (value) {
      case "VeryNegative":
      case "Negative":
      case "Fatal":
        return ValuationType.NEGATIVE;
      case "Neutral":
        return ValuationType.NEUTRAL;
      case "Good":
      case "VeryGood":
        return ValuationType.POSITIVE;
      default:
        return ValuationType.OTHER;
    }
  };

  useEffect(() => {
    setContentDimension({
      width: windowSizeContext.dimension.width,
      height: windowSizeContext.dimension.height - windowPadding,
    });
  }, [windowSizeContext.dimension.height, windowSizeContext.dimension.width]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; feedback?: unknown };
      if (!data || data.type !== "applyFeedbackMessages") {
        return;
      }
      const entries = Array.isArray(data.feedback) ? (data.feedback as PreviewFeedbackEntry[]) : [];
      const feedbackById: Record<string, FeedbackMessage[]> = {};
      const unresolvedFeedback: FeedbackMessage[] = [];
      entries.forEach((entry) => {
        const elementId = typeof entry.elementId === "string" ? entry.elementId : undefined;
        const content = typeof entry.message === "string" ? entry.message : "";
        if (!content) {
          return;
        }
        const message: FeedbackMessage = {
          category: typeof entry.category === "string" ? entry.category : "Feedback",
          valuation: mapTypeToValuation(entry.type),
          content,
          position: elementId,
        };
        if (elementId) {
          if (!feedbackById[elementId]) {
            feedbackById[elementId] = [];
          }
          feedbackById[elementId].push(message);
        } else {
          unresolvedFeedback.push(message);
        }
      });
      feedbackContext.setFeedback(feedbackById);
      feedbackContext.setUnresolvedFeedback(unresolvedFeedback);
      feedbackContext.setPositionFeedback([]);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [feedbackContext]);

  return (
    <>
      <ContentContainer width={contentDimension.width} height={contentDimension.height}>
        <ContentView
          width={contentDimension.width}
          height={contentDimension.height}
          modelingGraph={keaGraphContext.modelingGraph}
          modelingPaper={keaGraphContext.modelingPaper}
          apiManager={apiContext.apiManager}
          toggleErrorModal={errorModalContext.toggleErrorModal}
          keaGraphContext={keaGraphContext}
          setNode={feedbackModalContext.setNode}
        />
      </ContentContainer>
    </>
  );
};
